/**
 * Browser UAMP WebSocket client. Connects to Robutler /ws with JWT,
 * uses session multiplexing (session.create with conversation_id).
 * Use with useUAMP() hook. Runs in browser only (uses global WebSocket).
 */

import {
  createSessionCreate,
  createInputTyping,
  createInputAudio,
  parseEvent,
  serializeEvent,
} from './events';
import { UAMP_VERSION } from './types';
import type { ClientEvent, ServerEvent, AudioFormat } from './events';

const PING_INTERVAL_MS = 55_000;

/**
 * PER-MESSAGE TRACING IS OFF UNLESS A TAB ASKS FOR IT (round 7, lane 4,
 * item 3). Every frame used to print `[UAMPClient] handleMessage: ...` (plus
 * a line per streamed delta and per emit) in every tab of every environment;
 * the round 6 dev run (2026-09-27, polish 15) read each `workspace.bus` frame
 * "four times in the same second" in one tab and asked whether that was four
 * clients. It is not: a tab holds ONE client (`window.__uampClient`,
 * components/uamp-provider.tsx) with ONE socket (`connect` below closes any
 * earlier one before opening; the provider reconnects only when the client
 * is neither connected nor connecting), and each frame is logged once, before
 * any listener fan-out, so four lines are four frames on that one socket, or
 * the console capture repeating them; never four sockets. The load was never
 * there; the noise was. To trace one tab:
 * `localStorage.setItem('uamp:debug', '1')`, then reload.
 * tests/unit/realtime/r7-uamp-one-socket-quiet-log.test.tsx
 */
export const UAMP_DEBUG_STORAGE_KEY = 'uamp:debug';

function uampTraceEnabled(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage?.getItem(UAMP_DEBUG_STORAGE_KEY) === '1';
  } catch {
    // Storage blocked (opaque origin, privacy mode): stay quiet.
    return false;
  }
}

export type PortalMeta = { chatId?: string; messageId?: string; agentId?: string };

export type UAMPClientEventMap = {
  'chat:message': (message: Record<string, unknown>) => void;
  'chat:message_updated': (message: Record<string, unknown>) => void;
  'chat:message_deleted': (data: { messageId: string; chatId: string }) => void;
  // Emitted when the server pushes `chat.created` (e.g. a bridge accepts an
  // inbound message and synthesizes a portal chat for the owner).
  'chat:created': (chat: Record<string, unknown>) => void;
  'chat:typing': (data: { chatId: string; userId: string; username?: string; isTyping: boolean }) => void;
  'chat:agent_unreachable': (data: { chatId: string; agentUsername: string; reason?: string }) => void;
  'response.delta': (data: { response_id: string; delta: { type?: string; text?: string }; _portal?: PortalMeta }) => void;
  'response.done': (data: { response_id?: string; _portal?: PortalMeta }) => void;
  'response.cancelled': (data: { response_id?: string }) => void;
  'response.error': (data: { error: { code?: string; message?: string; details?: Record<string, unknown> }; _portal?: PortalMeta }) => void;
  'audio.delta': (data: { audio: string; format?: string }) => void;
  'transcript.delta': (data: { text: string; is_final?: boolean }) => void;
  'voice.invite': (data: { chatId: string; fromUserId: string; fromUsername: string }) => void;
  'voice.accept': (data: { chatId: string; fromUserId: string }) => void;
  'voice.decline': (data: { chatId: string; fromUserId: string }) => void;
  'voice.end': (data: { chatId: string }) => void;
  'presence:online': (data: { userId: string; username: string }) => void;
  'presence:offline': (data: { userId: string; username: string }) => void;
  'content.updated': (data: { content_id: string; command: string; diff?: { old_str?: string; new_str?: string; insert_line?: number }; timestamp: number }) => void;
  'notification.new': (data: { notification: { id: string; type: string; data?: Record<string, unknown>; [k: string]: unknown } }) => void;
  'notification.critical': (data: { notification: { id: string; type: string; data?: Record<string, unknown>; [k: string]: unknown } }) => void;
  'notification.read': (data: { all?: boolean; notificationId?: string; actionTaken?: string }) => void;
  /**
   * Workspace-scoped bus frame. Server fans these to every workspace
   * member's per-user channel; clients filter by `workspaceId` and
   * `itemId` to pick the relevant ones (see
   * `lib/workspaces/use-workspace-bus.ts`).
   */
  'workspace.bus': (frame: {
    workspaceId: string;
    itemId: string;
    kind: string;
    key?: string;
    value?: unknown;
    payload?: unknown;
    origin?: string | null;
  }) => void;
  'uamp:event': (event: ServerEvent) => void;
};

function getWsBaseUrl(): string {
  if (typeof window === 'undefined') return '';
  const u = window.location;
  return `${u.protocol === 'https:' ? 'wss:' : 'ws:'}//${u.host}`;
}

export class UAMPBrowserClient {
  private ws: WebSocket | null = null;
  private token: string | null = null;
  private sessionByChatId = new Map<string, string>();
  private listeners: { [K in keyof UAMPClientEventMap]?: Set<UAMPClientEventMap[K]> } = {};
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private _isConnected = false;
  private _connGen = 0; // monotonic generation counter to ignore stale WS events
  private _authRejected = false; // the server closed with 4001; see takeAuthRejected
  /** Read once per client (a tab reloads to change it); see UAMP_DEBUG_STORAGE_KEY. */
  private readonly trace = uampTraceEnabled();

  get isConnected(): boolean {
    return this._isConnected && this.ws?.readyState === WebSocket.OPEN;
  }

  get isConnecting(): boolean {
    return this.ws?.readyState === WebSocket.CONNECTING;
  }

  connect(token: string): void {
    // Already open with same token
    if (this.ws?.readyState === WebSocket.OPEN && this.token === token) return;
    // Still handshaking
    if (this.ws?.readyState === WebSocket.CONNECTING) return;

    // Tear down any prior WS (OPEN with different token, CLOSING, CLOSED)
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = null;
    }

    this.token = token;
    const gen = ++this._connGen;
    const base = getWsBaseUrl();
    const url = `${base}/ws?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      if (gen !== this._connGen) return; // stale
      this._isConnected = true;
      this.startPing();
    };
    ws.onclose = (ev?: { code?: number }) => {
      if (gen !== this._connGen) return; // stale
      this._isConnected = false;
      this.stopPing();
      this.sessionByChatId.clear();
      if (ev?.code === 4001) this._authRejected = true;
    };
    ws.onerror = () => {};
    ws.onmessage = (ev) => {
      if (gen !== this._connGen) return;
      this.handleMessage(ev.data);
    };
  }

  /**
   * Whether the server refused this client's token since the last ask, cleared by asking. The provider keeps the session token for
   * the life of the tab and redials with it every 5 s, so a token that expired or was rotated was retried forever: the tab had no
   * socket, no chat replies and no agent commands until a reload, while its HTTP presence kept reporting it connected.
   */
  takeAuthRejected(): boolean {
    const r = this._authRejected;
    this._authRejected = false;
    return r;
  }

  disconnect(): void {
    this.stopPing();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.token = null;
    this._isConnected = false;
    this.sessionByChatId.clear();
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({ type: 'ping', event_id: `evt_${Date.now()}`, timestamp: Date.now() });
      }
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private send(ev: ClientEvent | { type: string; event_id: string; timestamp?: number; session_id?: string }): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(serializeEvent(ev as ClientEvent));
  }

  private handleMessage(raw: string): void {
    // Try envelope format first: { event, chatId, _portal }
    let event: ServerEvent;
    let portal: PortalMeta | undefined;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && 'event' in parsed && parsed.event?.type) {
        event = parsed.event as ServerEvent;
        portal = parsed._portal as PortalMeta | undefined;
        // Always promote the envelope-level chatId into _portal.chatId so
        // event handlers can filter by chat without depending on the server
        // having attached a _portal block. Without this, broadcasts that omit
        // meta (e.g. delegate sub-chat response.done from per-agent-uamp.ts)
        // would fall through every chat-view's handler with no chat scoping.
        if (parsed.chatId) {
          if (!portal) portal = { chatId: parsed.chatId };
          else portal.chatId = parsed.chatId;
        }
      } else {
        // Fallback: raw UAMP event (backward compat)
        const evt = parseEvent(raw);
        if (!evt) return;
        event = evt as ServerEvent;
      }
    } catch {
      const evt = parseEvent(raw);
      if (!evt) return;
      event = evt as ServerEvent;
    }

    if (this.trace) {
      const _evType = (event as { type?: string }).type;
      if (_evType !== 'pong') {
        console.log(`[UAMPClient] handleMessage: type=${_evType} raw=${raw.length} bytes`);
      }
    }

    if (event.type === 'session.created') {
      const ev = event as ServerEvent & { session_id?: string; session?: { conversation_id?: string } };
      const sessionId = ev.session_id ?? (ev.session as { id?: string } | undefined)?.id;
      const conversationId = (ev.session as { conversation_id?: string } | undefined)?.conversation_id;
      if (sessionId && conversationId) this.sessionByChatId.set(conversationId, sessionId);
      return;
    }

    if (event.type === 'message.created') {
      const ev = event as ServerEvent & { message: Record<string, unknown> };
      this.emit('chat:message', ev.message);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('newMessage', { detail: ev.message }));
      }
      return;
    }

    if (event.type === 'message.updated') {
      const ev = event as ServerEvent & { message: Record<string, unknown> };
      if (this.trace) {
        const m = ev.message as { id?: string; chatId?: string; content?: string; contentItems?: unknown[]; metadata?: { status?: string } };
        const contentStr = typeof m?.content === 'string' ? m.content : '';
        console.log(`[stream-dbg] UAMPClient.recv message.updated: messageId=${m?.id ?? 'none'} chatId=${m?.chatId ?? 'none'} status=${m?.metadata?.status ?? 'none'} contentLen=${contentStr.length} contentItems=${m?.contentItems?.length ?? 0} preview="${contentStr.slice(0, 60).replace(/\n/g, '\\n')}"`);
      }
      this.emit('chat:message_updated', ev.message);
      return;
    }

    if (event.type === 'message.deleted') {
      const ev = event as ServerEvent & { message_id: string; chat_id: string };
      this.emit('chat:message_deleted', { messageId: ev.message_id, chatId: ev.chat_id });
      return;
    }

    if (event.type === 'chat.created') {
      const ev = event as ServerEvent & { chat: Record<string, unknown> };
      this.emit('chat:created', ev.chat);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('chatCreated', { detail: ev.chat }));
      }
      return;
    }

    if (event.type === 'presence.typing') {
      const ev = event as ServerEvent & { user_id: string; username?: string; is_typing: boolean; chat_id?: string; conversation_id?: string };
      this.emit('chat:typing', {
        chatId: ev.chat_id ?? ev.conversation_id ?? '',
        userId: ev.user_id,
        username: ev.username,
        isTyping: ev.is_typing,
      });
      return;
    }

    // Extension events not in the typed UAMP spec
    const eventType = event.type as string;

    if (eventType === 'presence.online') {
      const ev = event as ServerEvent & { user_id: string; username: string };
      this.emit('presence:online', { userId: ev.user_id, username: ev.username });
      return;
    }

    if (eventType === 'presence.offline') {
      const ev = event as ServerEvent & { user_id: string; username: string };
      this.emit('presence:offline', { userId: ev.user_id, username: ev.username });
      return;
    }

    if (eventType === 'response.delta') {
      const ev = event as ServerEvent & { response_id?: string; delta?: { type?: string; text?: string } };
      if (this.trace) {
        const toolName = ev.delta?.type === 'tool_call' ? ` tool=${(ev.delta as any).tool_call?.name}` : '';
        console.log(`[UAMPClient] response.delta: response_id=${ev.response_id} delta.type=${ev.delta?.type}${toolName} text=${ev.delta?.text?.length ?? 0} chars`);
      }
      if (ev.response_id && ev.delta) {
        this.emit('response.delta', { response_id: ev.response_id, delta: ev.delta, _portal: portal });
      }
      return;
    }

    if (eventType === 'audio.delta') {
      const ev = event as ServerEvent & { audio: string; format?: string };
      this.emit('audio.delta', { audio: ev.audio, format: ev.format });
      return;
    }

    if (eventType === 'transcript.delta') {
      const ev = event as ServerEvent & { text: string; is_final?: boolean };
      this.emit('transcript.delta', { text: ev.text, is_final: ev.is_final });
      return;
    }

    if (eventType === 'voice.invite') {
      const ev = event as ServerEvent & { conversation_id: string; from_user_id: string; from_username: string };
      this.emit('voice.invite', { chatId: ev.conversation_id, fromUserId: ev.from_user_id, fromUsername: ev.from_username });
      return;
    }

    if (eventType === 'voice.accept') {
      const ev = event as ServerEvent & { conversation_id: string; from_user_id: string };
      this.emit('voice.accept', { chatId: ev.conversation_id, fromUserId: ev.from_user_id });
      return;
    }

    if (eventType === 'voice.decline') {
      const ev = event as ServerEvent & { conversation_id: string; from_user_id: string };
      this.emit('voice.decline', { chatId: ev.conversation_id, fromUserId: ev.from_user_id });
      return;
    }

    if (eventType === 'voice.end') {
      const ev = event as ServerEvent & { conversation_id: string };
      this.emit('voice.end', { chatId: ev.conversation_id });
      return;
    }

    if (eventType === 'content.updated') {
      const ev = event as ServerEvent & { content_id: string; command: string; diff?: { old_str?: string; new_str?: string; insert_line?: number }; timestamp: number };
      this.emit('content.updated', { content_id: ev.content_id, command: ev.command, diff: ev.diff, timestamp: ev.timestamp });
      return;
    }

    if (eventType === 'response.done') {
      const ev = event as ServerEvent & { response_id?: string };
      if (this.trace) console.log(`[UAMPClient] response.done: response_id=${ev.response_id}`);
      this.emit('response.done', { response_id: ev.response_id, _portal: portal });
      return;
    }

    if (eventType === 'response.cancelled') {
      const ev = event as ServerEvent & { response_id?: string };
      this.emit('response.cancelled', { response_id: ev.response_id });
      return;
    }

    if (eventType === 'notification.new') {
      const ev = event as ServerEvent & { notification: { id: string; type: string; data?: Record<string, unknown> } };
      this.emit('notification.new', { notification: ev.notification });
      this.emit('uamp:event', event as ServerEvent);
      return;
    }

    if (eventType === 'notification.critical') {
      const ev = event as ServerEvent & { notification: { id: string; type: string; data?: Record<string, unknown> } };
      this.emit('notification.critical', { notification: ev.notification });
      this.emit('uamp:event', event as ServerEvent);
      return;
    }

    if (eventType === 'notification.read') {
      const ev = event as ServerEvent & { all?: boolean; notificationId?: string; actionTaken?: string };
      this.emit('notification.read', { all: ev.all, notificationId: ev.notificationId, actionTaken: ev.actionTaken });
      this.emit('uamp:event', event as ServerEvent);
      return;
    }

    if (eventType === 'workspace.bus') {
      // Server publishes `{ event: { type: 'workspace.bus', payload: frame } }`
      // via the same uamp:user:{userId} channel every other event uses.
      // We unwrap and re-emit on our typed bus.
      const ev = event as ServerEvent & {
        payload?: {
          workspaceId?: string;
          itemId?: string;
          kind?: string;
          key?: string;
          value?: unknown;
          payload?: unknown;
          origin?: string | null;
        };
      };
      const p = ev.payload;
      if (
        p &&
        typeof p.workspaceId === 'string' &&
        typeof p.itemId === 'string' &&
        typeof p.kind === 'string'
      ) {
        this.emit('workspace.bus', {
          workspaceId: p.workspaceId,
          itemId: p.itemId,
          kind: p.kind,
          key: p.key,
          value: p.value,
          payload: p.payload,
          origin: p.origin ?? null,
        });
      }
      this.emit('uamp:event', event as ServerEvent);
      return;
    }

    if (eventType === 'response.error') {
      const ev = event as ServerEvent & { error?: { code?: string; message?: string; details?: Record<string, unknown> } };
      if (ev.error) {
        this.emit('response.error', { error: ev.error, _portal: portal });
        // Also emit as chat:agent_unreachable if the error has agent details
        const details = ev.error.details as { agent_username?: string; agent_id?: string } | undefined;
        if (details?.agent_username) {
          this.emit('chat:agent_unreachable', {
            chatId: '', // broadcast-level, frontend filters by chatId anyway
            agentUsername: details.agent_username,
            reason: ev.error.message,
          });
        }
      }
      return;
    }

    this.emit('uamp:event', event as ServerEvent);
  }

  private emit<K extends keyof UAMPClientEventMap>(event: K, payload: Parameters<UAMPClientEventMap[K]>[0]): void {
    const set = this.listeners[event];
    if (this.trace && (event === 'response.delta' || event === 'response.done' || event === 'chat:message')) {
      console.log(`[UAMPClient] emit(${String(event)}): ${set?.size ?? 0} listeners`);
    }
    if (set) set.forEach((cb) => (cb as (p: unknown) => void)(payload));
  }

  on<K extends keyof UAMPClientEventMap>(event: K, callback: UAMPClientEventMap[K]): () => void {
    if (!this.listeners[event]) (this.listeners as Record<string, Set<unknown>>)[event as string] = new Set();
    this.listeners[event]!.add(callback);
    return () => this.listeners[event]?.delete(callback);
  }

  joinChat(chatId: string): void {
    const sessionCreate = createSessionCreate(UAMP_VERSION, {
      extensions: { conversation_id: chatId } as Record<string, unknown>,
    }, {
      supports_rich_display: true,
    });
    this.send(sessionCreate);
  }

  leaveChat(chatId: string): void {
    const sessionId = this.sessionByChatId.get(chatId);
    if (sessionId) {
      this.send({ type: 'session.end', event_id: `evt_${Date.now()}`, timestamp: Date.now(), session_id: sessionId } as unknown as ClientEvent);
      this.sessionByChatId.delete(chatId);
    }
  }

  sendTyping(chatId: string, isTyping: boolean): void {
    const ev = createInputTyping(isTyping, chatId);
    const sessionId = this.sessionByChatId.get(chatId);
    if (sessionId) (ev as { session_id?: string }).session_id = sessionId;
    this.send(ev);
  }

  markRead(_chatId: string): void {
    // UAMP: could send a message.read event if server supports it; no-op for now
  }

  sendAudio(chatId: string, base64: string, format?: string, isFinal?: boolean): void {
    const ev = createInputAudio(base64, (format as AudioFormat) || 'webm', isFinal);
    const sessionId = this.sessionByChatId.get(chatId);
    if (sessionId) (ev as { session_id?: string }).session_id = sessionId;
    this.send(ev);
  }

  sendVoiceInvite(chatId: string): void {
    this.send({
      type: 'voice.invite',
      event_id: `evt_${Date.now()}`,
      timestamp: Date.now(),
      conversation_id: chatId,
    } as unknown as ClientEvent);
  }

  sendVoiceAccept(chatId: string): void {
    this.send({
      type: 'voice.accept',
      event_id: `evt_${Date.now()}`,
      timestamp: Date.now(),
      conversation_id: chatId,
    } as unknown as ClientEvent);
  }

  sendVoiceEnd(chatId: string): void {
    this.send({
      type: 'voice.end',
      event_id: `evt_${Date.now()}`,
      timestamp: Date.now(),
      conversation_id: chatId,
    } as unknown as ClientEvent);
  }

  subscribeContent(contentId: string): void {
    this.send({
      type: 'content.subscribe',
      event_id: `evt_${Date.now()}`,
      timestamp: Date.now(),
      content_id: contentId,
    } as unknown as ClientEvent);
  }

  unsubscribeContent(contentId: string): void {
    this.send({
      type: 'content.unsubscribe',
      event_id: `evt_${Date.now()}`,
      timestamp: Date.now(),
      content_id: contentId,
    } as unknown as ClientEvent);
  }

  sendCancel(chatId: string, responseId?: string): void {
    const sessionId = this.sessionByChatId.get(chatId);
    this.send({
      type: 'response.cancel',
      event_id: `evt_${Date.now()}`,
      timestamp: Date.now(),
      session_id: sessionId,
      response_id: responseId,
    } as unknown as ClientEvent);
  }

  /**
   * Reply uplink for the `workspace.widgets.*` RPC. Called by
   * `<WorkspaceAgentCommandReceiver/>` after dispatching an
   * `agent.command` frame through the local `WidgetCommandBus`. The
   * server matches the reply against its pending Promise (see
   * `lib/realtime/workspace-agent-rpc.ts`) and resolves the agent's
   * tool call. Best-effort — when the socket is not OPEN the reply
   * is silently dropped and the agent's RPC times out, which is the
   * correct failure mode (we'd rather time out than wedge the agent
   * waiting forever).
   */
  sendWorkspaceBusReply(reply: {
    workspaceId: string;
    requestId: string;
    ok: boolean;
    result?: unknown;
    error?: { code: string; message: string };
  }): void {
    this.send({
      type: 'workspace.bus.reply',
      event_id: `evt_${Date.now()}`,
      timestamp: Date.now(),
      ...reply,
    } as unknown as ClientEvent);
  }
}
